import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRightLong } from "@fortawesome/free-solid-svg-icons"
import { AuditChange } from "types/questionnaire.types"
import {
  changeJumpTarget,
  changeQuestion,
  changeTitle,
  isCodeRemap,
  remapNote,
  reorderNote,
} from "../utils/historyLabels"

/**
 * Renders a single metadata change (one `AuditChange`) as a delta row.
 * Shared by the Version History panel (per-version diff) and the Tracker
 * comparison panel (cross-project diff) so both speak the same visual language.
 */

// Semantic styling per change kind.
export type ChangeVisual = { symbol: string; label: string; dot: string; chip: string }

export const changeVisual = (kind: string): ChangeVisual => {
  // The data.cleaned kinds. These describe CELLS, not metadata: a clean blanks answers the
  // routing never asked for, so "cleaned" is a deliberate emptying (teal) rather than a removal
  // (rose) or an edit (amber).
  if (kind === "cleaned")
    return { symbol: "∅", label: "cleaned", dot: "bg-teal-500", chip: "bg-teal-50 text-teal-700 border-teal-200" }
  // A refused column — claimed by more than one question, so blanking it under one question's
  // routing would silently apply that routing to another question's answers. Nothing happened to
  // it, so it gets an informational tone, never a warning one: the refusal is the safe outcome.
  if (kind === "skipped")
    return { symbol: "=", label: "not cleaned — shared column", dot: "bg-slate-300", chip: "bg-slate-50 text-slate-600 border-slate-200" }
  // The 500-entry cap marker. Muted, because it is a note about the LIST, not about the data —
  // the record's summary counts stay exact past the cap.
  if (kind === "truncated")
    return { symbol: "…", label: "list truncated", dot: "bg-slate-200", chip: "bg-white text-slate-400 border-slate-200" }
  if (kind.endsWith(".added"))
    return { symbol: "+", label: "added", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" }
  if (kind.endsWith(".removed"))
    return { symbol: "−", label: "removed", dot: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200" }
  if (kind.endsWith(".reordered"))
    return { symbol: "↕", label: "reordered", dot: "bg-slate-400", chip: "bg-slate-50 text-slate-600 border-slate-200" }
  if (isCodeRemap(kind))
    return { symbol: "~", label: "codes changed", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200" }
  // any *.changed (label / text / type / option.label)
  return { symbol: "~", label: kind.replace(/^[a-z]+\./, "").replace(/\./g, " "), dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200" }
}

/**
 * @param onJump - when provided, the row becomes clickable and invokes this to jump to the
 *                 affected question in the editor (only wired where the project is open).
 */
export const ChangeRow: React.FC<{ change: AuditChange; onJump?: () => void }> = ({ change, onJump }) => {
  const v = changeVisual(change.kind)
  // A code remap carries the old and new codes: never shown, the row says the codes changed.
  const remap = remapNote(change)
  const showDelta = !remap && (change.oldValue !== undefined || change.newValue !== undefined)
  // For option-level changes the engine sets `path` to the owning question while `label` is the
  // option text: surface that question so "Non-Binary · added" reads as "added in Gender".
  // A change inside a variant has the path "<variant>/<question>": the title names the variant.
  const inQuestion = changeQuestion(change)
  // An answer / row / column reorder or code remap: say what changed in the question's list.
  const listNote = reorderNote(change.kind) ?? remap

  const body = (
    <>
      <div className="flex items-center gap-2">
        <span className="truncate text-[13px] font-medium text-slate-800">{changeTitle(change)}</span>
        <span className={`flex-none rounded border px-1.5 py-px text-[10px] font-medium uppercase tracking-wide ${v.chip}`}>
          {v.label}
        </span>
        {onJump && (
          <FontAwesomeIcon
            icon={faArrowRightLong}
            className="ml-auto h-3 w-3 flex-none text-slate-300 opacity-0 transition-opacity group-hover:opacity-100"
          />
        )}
      </div>
      {inQuestion && (
        <div className="truncate text-[11px] text-slate-400">
          in <span className="font-medium text-slate-500">{inQuestion}</span>
        </div>
      )}
      {listNote ? (
        <div className="mt-0.5 text-[12px] leading-snug text-slate-500">{listNote}</div>
      ) : showDelta && change.kind.endsWith(".reordered") ? (
        // Meaning unchanged: a neutral "moved" line, not a rose/emerald value delta.
        <div className="mt-0.5 text-[12px] leading-snug text-slate-500">
          moved
          {change.oldValue !== undefined && change.newValue !== undefined
            ? ` · position ${change.oldValue} → ${change.newValue}`
            : change.newValue !== undefined
              ? ` · to position ${change.newValue}`
              : ""}
        </div>
      ) : showDelta ? (
        <div className="mt-0.5 text-[12px] leading-snug text-slate-500">
          {change.oldValue !== undefined && (
            <span className="text-rose-500 line-through decoration-rose-300">{String(change.oldValue) || "(empty)"}</span>
          )}
          {change.oldValue !== undefined && change.newValue !== undefined && <span className="mx-1.5 text-slate-300">→</span>}
          {change.newValue !== undefined && <span className="text-emerald-600">{String(change.newValue) || "(empty)"}</span>}
        </div>
      ) : null}
    </>
  )

  return (
    <li className="flex gap-2.5 py-1.5">
      <span className={`mt-1.5 h-1.5 w-1.5 flex-none rounded-full ${v.dot}`} />
      {onJump ? (
        <button
          type="button"
          onClick={onJump}
          title={`Jump to ${changeJumpTarget(change) || "this item"} in the editor`}
          className="group -my-0.5 -mr-1 min-w-0 flex-1 cursor-pointer rounded px-1 py-0.5 text-left transition-colors hover:bg-slate-50"
        >
          {body}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
    </li>
  )
}

export default ChangeRow
